import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Sparkles, Utensils, Music, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Schedule = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const days = [
    {
      date: t('saturday'),
      isMainDay: true,
      subtitle: t('weddingCelebration'),
      events: [
        { icon: Sparkles, title: t('cocktailHour'), time: t('timeCocktail'), desc: t('cocktailHourDesc') },
        { icon: Utensils, title: t('dinner'), time: t('timeDinner'), desc: t('dinnerDesc') },
        { icon: Music, title: t('soiree'), time: t('timeDance'), desc: t('soireeDesc') },
      ],
    },
  ];

  return (
    <section ref={ref} className="py-20 bg-white" data-testid="schedule-section">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }} className="text-center mb-16">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A2B3D] mb-4">{t('schedule')}</h2>
          <div className="wave-divider" />
        </motion.div>

        <div className="space-y-16">
          {days.map((day, dayIndex) => (
            <motion.div key={day.date} initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + dayIndex * 0.15 }}>
              <div className={`mb-8 pb-4 border-b border-[#CBD5E1] ${day.isMainDay ? 'text-center' : ''}`}>
                <h3 className="font-serif text-2xl text-[#1A2B3D] mb-1">{day.date}</h3>
                {day.subtitle && (
                  <p className="text-[#C9A96E] text-sm uppercase tracking-[0.15em] mb-2">{day.subtitle}</p>
                )}
                {day.isMainDay && (
                  <p className="flex items-center justify-center gap-1.5 text-[#6B8299] text-sm">
                    <MapPin className="w-3.5 h-3.5 text-[#C9A96E]" /> {t('venueAddress')}
                  </p>
                )}
              </div>

              <div className="space-y-8">
                {day.events.map((event) => (
                  <div key={event.title} className="flex gap-6">
                    <div className="w-24 flex-shrink-0 text-right">
                      <span className="text-[#1E3A5F] font-medium text-sm">{event.time}</span>
                    </div>
                    <div className="flex-1 pb-8 border-l-2 border-[#CBD5E1] pl-6 relative">
                      <div className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-[#C9A96E]" />
                      <div className="flex items-center gap-2 mb-2">
                        <event.icon className="w-4 h-4 text-[#C9A96E]" />
                        <h4 className="font-serif text-xl text-[#1A2B3D]">{event.title}</h4>
                      </div>
                      {event.subtitle && (
                        <p className="text-[#6B8299] text-xs uppercase tracking-wider mb-1">{event.subtitle}</p>
                      )}
                      {event.desc && (
                        <p className="text-[#6B8299] text-sm leading-relaxed">{event.desc}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
